import { FC, isValidElement, ReactNode } from 'react'
import { logger } from 'helpers/logger'
import { authSlice } from '@wellesley/auth/authSlice'
import { useSelector } from 'react-redux'

import { getErrorMsg } from '../helpers/getErrorMsg'
import {
  ErrorBody,
  isErrorBody,
  isStatusError,
  getErrorByStatusCode,
} from '../helpers/getErrorByStatusCode'

import icon404 from './404.png'
import { Links } from './Links'

export type ErrorPageProps = {
  error: unknown
  icon?: ReactNode
}

const getError = (error: unknown, isLogged: boolean) => {
  switch (true) {
    case isErrorBody(error):
      return error
    case isStatusError(error): {
      error === 500 && logger.error('ErrorPage: ', error)
      return getErrorByStatusCode(error, isLogged)
    }
    case error && !!getErrorMsg(error):
      return { title: getErrorMsg(error) } as ErrorBody
    default: {
      logger.error('ErrorPage: ', error)
      return getErrorByStatusCode(500, isLogged)
    }
  }
}

export const ErrorPage: FC<ErrorPageProps> = ({ error, icon }) => {
  const isLogged = useSelector(authSlice.selectors.selectIsAuthenticated)

  const { title, description, links } = getError(error, isLogged)
  const isElementDescription = isValidElement(description)

  return (
    <div
      data-testid="error-page"
      className="flex min-h-[100vh] items-center justify-center"
    >
      <div className="gap-l flex max-w-[490px] translate-y-[-10%] flex-col items-center justify-center">
        {icon ? icon : <img src={icon404} />}
        <div className="gap-s flex flex-col items-center justify-center">
          {title && (
            <h1
              data-testid="error-page-title"
              className="text-center text-2xl font-semibold text-gray-900"
            >
              {title}
            </h1>
          )}
          {description && (
            <p
              data-testid="error-page-description"
              className="max-w-[343px] text-center text-base font-medium text-gray-900"
              {...(!isElementDescription && {
                dangerouslySetInnerHTML: { __html: description },
              })}
            >
              {isElementDescription ? description : null}
            </p>
          )}
        </div>
        {links?.length ? <Links linksList={links} /> : null}
      </div>
    </div>
  )
}
